import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "@tanstack/react-form";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useState } from "react";
import { useUpdateStaffLevel } from "@/hooks/staff/use-staff-levels";
import type { StaffLevel } from "@/types/staff/api-types/staff-level-types";
import { addStaffLevelSchema } from "@/zod-schemas/staff/add-staff-level-schema";
import { getStaffLevelFormValues } from "@/default-form-values/staff/default-staff-level-form-values";

interface EditStaffLevelDialogProps {
  staffLevel: StaffLevel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditStaffLevelDialog({
  staffLevel,
  open,
  onOpenChange,
}: EditStaffLevelDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const updateStaffLevel = useUpdateStaffLevel();

  const form = useForm({
    defaultValues: getStaffLevelFormValues(staffLevel),
    validators: {
      onSubmit: addStaffLevelSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addStaffLevelSchema.parse(value);
        await updateStaffLevel.mutateAsync({
          id: staffLevel.id,
          data: {
            name: validated.name.trim(),
          },
        });
        toast.success(`Staff level "${validated.name}" updated successfully`);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;
        
        if (statusCode === 409) {
          setServerError(`A staff level with the name "${value.name}" already exists`);
        } else {
          toast.error(`Failed to update staff level "${value.name}"`);
          onOpenChange(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Staff Level</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-staff-level-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />

          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="edit-staff-level-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}